import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Panel, Btn, PageHead, StatusDot } from "@/components/kit";
import { BrainCircuit, Youtube, Clapperboard, Cpu, Film, Copy, Zap, ExternalLink } from "lucide-react";

const Field = ({ label, value, onChange, placeholder, type = "text", testId }) => (
    <div className="mb-3">
        <div className="label mb-1">{label}</div>
        <input
            data-testid={testId}
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        />
    </div>
);

const StatusLine = ({ configured, status, detail }) => {
    const color = !configured ? "#4A4A4A" : status === "ok" ? "#39FF14" : status === "error" ? "#FF003C" : "#FFD700";
    const text = !configured ? "SIN CONFIGURAR" : status === "ok" ? "CONECTADO" : status === "error" ? "ERROR" : "GUARDADO · SIN PROBAR";
    return (
        <div className="mb-4 flex items-center gap-2 text-[10px] uppercase tracking-widest" style={{ color }}>
            <StatusDot color={color} pulse={status === "ok"} /> {text}
            {detail && <span className="normal-case tracking-normal text-muted-foreground">· {detail}</span>}
        </div>
    );
};

export default function Integrations() {
    const qc = useQueryClient();
    const { data } = useQuery({
        queryKey: ["integrations"],
        queryFn: async () => (await api.get("/integrations")).data,
    });
    const mm = data?.minimax || {};
    const gm = data?.gemini || {};
    const nv = data?.nvidia || {};
    const hf = data?.higgsfield || {};
    const fl = data?.fal || {};
    const g = data?.google || {};
    const px = data?.pixelle || {};

    const [mmKey, setMmKey] = useState("");
    const [gmKey, setGmKey] = useState("");
    const [nvKey, setNvKey] = useState("");
    const [hfId, setHfId] = useState("");
    const [falKey, setFalKey] = useState("");
    const [hfSecret, setHfSecret] = useState("");
    const [gid, setGid] = useState("");
    const [gsecret, setGsecret] = useState("");
    const [pxUrl, setPxUrl] = useState("");
    const [pxKey, setPxKey] = useState("");

    useEffect(() => {
        if (g.client_id) setGid(g.client_id);
        if (px.base_url) setPxUrl(px.base_url);
        // eslint-disable-next-line
    }, [data]);

    const save = useMutation({
        mutationFn: async (payload) =>
            (await api.put("/integrations", { ...payload, redirect_origin: window.location.origin })).data,
        onSuccess: () => {
            toast.success("Credenciales guardadas");
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo guardando"),
    });

    const testMm = useMutation({
        mutationFn: async () => (await api.post("/integrations/minimax/test")).data,
        onSuccess: (d) => {
            toast.success(`MiniMax M3 respondió: ${d.reply}`);
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
        onError: (e) => {
            toast.error(e?.response?.data?.detail || "MiniMax no respondió");
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
    });

    const testVeo = useMutation({
        mutationFn: async () => (await api.post("/integrations/gemini/test")).data,
        onSuccess: (d) => {
            toast.success(`Google Veo listo: ${d.detail}`);
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
        onError: (e) => {
            toast.error(e?.response?.data?.detail || "Google AI Studio no respondió");
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
    });

    const testNvidia = useMutation({
        mutationFn: async () => (await api.post("/integrations/nvidia/test")).data,
        onSuccess: (d) => {
            toast.success(`NVIDIA NIM listo: ${d.detail}`);
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
        onError: (e) => {
            toast.error(e?.response?.data?.detail || "NVIDIA NIM no respondió");
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
    });

    const testFal = useMutation({
        mutationFn: async () => (await api.post("/integrations/fal/test")).data,
        onSuccess: (d) => {
            toast.success(`fal.ai: ${d.detail}`);
            qc.invalidateQueries({ queryKey: ["integrations"] });
            qc.invalidateQueries({ queryKey: ["engines-status"] });
        },
        onError: (e) => {
            toast.error(e?.response?.data?.detail || "fal.ai no respondió");
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
    });

    const testHiggsfield = useMutation({        mutationFn: async () => (await api.post("/integrations/higgsfield/test")).data,
        onSuccess: (d) => {
            (d.ok ? toast.success : toast.warning)(`Higgsfield: ${d.detail}`);
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
        onError: (e) => {
            toast.error(e?.response?.data?.detail || "Higgsfield no respondió");
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
    });

    const testPx = useMutation({
        mutationFn: async () => (await api.post("/integrations/pixelle/test")).data,
        onSuccess: (d) => {
            toast.success(`Pixelle-Video online: ${d.detail}`);
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
        onError: (e) => {
            toast.error(e?.response?.data?.detail || "Pixelle inaccesible");
            qc.invalidateQueries({ queryKey: ["integrations"] });
        },
    });

    const oauth = useMutation({
        mutationFn: async () => (await api.get("/youtube/auth-url")).data,
        onSuccess: (d) => {
            window.location.href = d.url;
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Configura Google Cloud primero"),
    });

    const redirectUri = g.redirect_uri || `${window.location.origin}/api/youtube/callback`;

    return (
        <div data-testid="integrations-page">
            <PageHead
                title="INTEGRACIONES"
                sub="Conecta tus credenciales reales: MiniMax M3 (cerebro), Google Veo 3.1 (video cinematográfico), Google Cloud (YouTube OAuth + Analytics) y tu microservicio Pixelle-Video en el VPS."
            />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* ---- MiniMax ---- */}
                <Panel
                    title="MiniMax M3 · Cerebro"
                    testId="minimax-panel"
                    right={<BrainCircuit size={15} className="text-primary" />}
                >
                    <StatusLine configured={mm.configured} status={mm.status} />
                    <p className="mb-4 text-xs text-muted-foreground">
                        Con tu API key, guiones, nichos, kits y estrategia se generan con{" "}
                        <span className="text-primary">MiniMax-M3 real</span>. Sin key, se usa la Universal Key
                        (Claude) como fallback.
                    </p>
                    {mm.configured && (
                        <div className="mb-3 border border-border p-2 font-mono text-xs text-muted-foreground">
                            API key: {mm.key_mask} · modelo {mm.model}
                        </div>
                    )}
                    <Field
                        label="API Key de MiniMax"
                        value={mmKey}
                        onChange={setMmKey}
                        placeholder="sk-... (platform.minimax.io)"
                        type="password"
                        testId="minimax-key-input"
                    />
                    <div className="flex gap-2">
                        <Btn
                            testId="minimax-save-btn"
                            onClick={() => save.mutate({ minimax_api_key: mmKey })}
                            disabled={save.isPending || !mmKey.trim()}
                        >
                            Guardar
                        </Btn>
                        <Btn
                            testId="minimax-test-btn"
                            variant="ghost"
                            onClick={() => testMm.mutate()}
                            disabled={testMm.isPending || !mm.configured}
                        >
                            <span className="flex items-center gap-2">
                                <Zap size={13} /> {testMm.isPending ? "Probando…" : "Probar"}
                            </span>
                        </Btn>
                        {mm.configured && (
                            <Btn
                                testId="minimax-clear-btn"
                                variant="danger"
                                onClick={() => save.mutate({ minimax_api_key: "" })}
                            >
                                Quitar
                            </Btn>
                        )}
                    </div>
                </Panel>

                {/* ---- Google Veo (AI Studio) ---- */}
                <Panel
                    title="Google Veo 3.1 · Video Cinematográfico"
                    testId="veo-panel"
                    right={<Clapperboard size={15} className="text-primary" />}
                >
                    <StatusLine configured={gm.configured} status={gm.status} detail={gm.detail} />
                    <p className="mb-4 text-xs text-muted-foreground">
                        Con tu API key de{" "}
                        <a
                            href="https://aistudio.google.com/api-keys"
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline"
                        >
                            Google AI Studio
                        </a>
                        , los clips cinematográficos se generan con{" "}
                        <span className="text-primary">Veo 3.1 real</span>. Sin key, se usa Sora-2
                        (Universal Key) como motor de video.
                    </p>
                    {gm.configured && (
                        <div className="mb-3 border border-border p-2 font-mono text-xs text-muted-foreground">
                            API key: {gm.key_mask} · {gm.model}
                        </div>
                    )}
                    <Field
                        label="API Key de Google AI Studio"
                        value={gmKey}
                        onChange={setGmKey}
                        placeholder="AQ.... o AIza... (aistudio.google.com)"
                        type="password"
                        testId="veo-key-input"
                    />
                    <div className="flex gap-2">
                        <Btn
                            testId="veo-save-btn"
                            onClick={() => save.mutate({ gemini_api_key: gmKey })}
                            disabled={save.isPending || !gmKey.trim()}
                        >
                            Guardar
                        </Btn>
                        <Btn
                            testId="veo-test-btn"
                            variant="ghost"
                            onClick={() => testVeo.mutate()}
                            disabled={testVeo.isPending || !gm.configured}
                        >
                            <span className="flex items-center gap-2">
                                <Zap size={13} /> {testVeo.isPending ? "Probando…" : "Probar"}
                            </span>
                        </Btn>
                        {gm.configured && (
                            <Btn
                                testId="veo-clear-btn"
                                variant="danger"
                                onClick={() => save.mutate({ gemini_api_key: "" })}
                            >
                                Quitar
                            </Btn>
                        )}
                    </div>
                </Panel>

                {/* ---- NVIDIA NIM ---- */}
                <Panel
                    title="NVIDIA NIM · Texto Gratis (MiniMax M3)"
                    testId="nvidia-panel"
                    right={<Cpu size={15} className="text-primary" />}
                >
                    <StatusLine configured={nv.configured} status={nv.status} detail={nv.detail} />
                    <p className="mb-4 text-xs text-muted-foreground">
                        Con tu API key de{" "}
                        <a
                            href="https://build.nvidia.com/settings/api-keys"
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline"
                        >
                            build.nvidia.com
                        </a>
                        , los guiones, nichos y kits se generan <span className="text-primary">GRATIS
                        con MiniMax M3 vía NVIDIA</span> — tu saldo se reserva para el video.
                    </p>
                    {nv.configured && (
                        <div className="mb-3 border border-border p-2 font-mono text-xs text-muted-foreground">
                            API key: {nv.key_mask} · {nv.model}
                        </div>
                    )}
                    <Field
                        label="API Key de NVIDIA"
                        value={nvKey}
                        onChange={setNvKey}
                        placeholder="nvapi-... (build.nvidia.com)"
                        type="password"
                        testId="nvidia-key-input"
                    />
                    <div className="flex gap-2">
                        <Btn
                            testId="nvidia-save-btn"
                            onClick={() => save.mutate({ nvidia_api_key: nvKey })}
                            disabled={save.isPending || !nvKey.trim()}
                        >
                            Guardar
                        </Btn>
                        <Btn
                            testId="nvidia-test-btn"
                            variant="ghost"
                            onClick={() => testNvidia.mutate()}
                            disabled={testNvidia.isPending || !nv.configured}
                        >
                            <span className="flex items-center gap-2">
                                <Zap size={13} /> {testNvidia.isPending ? "Probando…" : "Probar"}
                            </span>
                        </Btn>
                        {nv.configured && (
                            <Btn
                                testId="nvidia-clear-btn"
                                variant="danger"
                                onClick={() => save.mutate({ nvidia_api_key: "" })}
                            >
                                Quitar
                            </Btn>
                        )}
                    </div>
                </Panel>

                {/* ---- fal.ai · LTX-2.3 Fast ---- */}
                <Panel
                    title="fal.ai · LTX-2.3 Fast (Video 1080p)"
                    testId="fal-panel"
                    right={<Film size={15} className="text-primary" />}
                >
                    <StatusLine configured={fl.configured} status={fl.status} detail={fl.detail} />
                    <p className="mb-4 text-xs text-muted-foreground">
                        Motor de video <span className="text-primary">$0.04/s en 1080p</span> (clip de 10s
                        = $0.40), soporta 16:9 y 9:16 nativo. Crea tu key en{" "}
                        <a
                            href="https://fal.ai/dashboard/keys"
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline"
                        >
                            fal.ai/dashboard/keys
                        </a>{" "}
                        (formato <span className="font-mono">key_id:secret</span>).
                    </p>
                    {fl.configured && (
                        <div className="mb-3 border border-border p-2 font-mono text-xs text-muted-foreground">
                            API key: {fl.key_mask} · {fl.model}
                        </div>
                    )}
                    <Field
                        label="API Key de fal.ai"
                        value={falKey}
                        onChange={setFalKey}
                        placeholder="key_id:secret"
                        type="password"
                        testId="fal-key-input"
                    />
                    <div className="flex gap-2">
                        <Btn
                            testId="fal-save-btn"
                            onClick={() => save.mutate({ fal_api_key: falKey })}
                            disabled={save.isPending || !falKey.trim()}
                        >
                            Guardar
                        </Btn>
                        <Btn
                            testId="fal-test-btn"
                            variant="ghost"
                            onClick={() => testFal.mutate()}
                            disabled={testFal.isPending || !fl.configured}
                        >
                            <span className="flex items-center gap-2">
                                <Zap size={13} /> {testFal.isPending ? "Probando…" : "Probar"}
                            </span>
                        </Btn>
                        {fl.configured && (
                            <Btn
                                testId="fal-clear-btn"
                                variant="danger"
                                onClick={() => save.mutate({ fal_api_key: "" })}
                            >
                                Quitar
                            </Btn>
                        )}
                    </div>
                </Panel>

                {/* ---- Higgsfield ---- */}
                <Panel
                    title="Higgsfield · Soul + DoP (Video Cinemático)"
                    testId="higgsfield-panel"
                    right={<Film size={15} className="text-primary" />}
                >
                    <StatusLine configured={hf.configured} status={hf.status} detail={hf.detail} />
                    <p className="mb-4 text-xs text-muted-foreground">
                        API oficial de{" "}
                        <a
                            href="https://cloud.higgsfield.ai"
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline"
                        >
                            cloud.higgsfield.ai
                        </a>
                        : imágenes cinematográficas Soul + movimiento de cámara DoP.
                        Requiere <span className="text-primary">créditos API</span> (se compran aparte).
                    </p>
                    {hf.configured && (
                        <div className="mb-3 border border-border p-2 font-mono text-xs text-muted-foreground">
                            Key ID: {hf.key_mask} · {hf.model}
                        </div>
                    )}
                    <Field
                        label="API Key ID"
                        value={hfId}
                        onChange={setHfId}
                        placeholder="uuid de cloud.higgsfield.ai"
                        type="password"
                        testId="higgsfield-id-input"
                    />
                    <Field
                        label="API Secret"
                        value={hfSecret}
                        onChange={setHfSecret}
                        placeholder="secret hexadecimal"
                        type="password"
                        testId="higgsfield-secret-input"
                    />
                    <div className="flex gap-2">
                        <Btn
                            testId="higgsfield-save-btn"
                            onClick={() => save.mutate({ higgsfield_key_id: hfId, higgsfield_key_secret: hfSecret })}
                            disabled={save.isPending || !hfId.trim() || !hfSecret.trim()}
                        >
                            Guardar
                        </Btn>
                        <Btn
                            testId="higgsfield-test-btn"
                            variant="ghost"
                            onClick={() => testHiggsfield.mutate()}
                            disabled={testHiggsfield.isPending || !hf.configured}
                        >
                            <span className="flex items-center gap-2">
                                <Zap size={13} /> {testHiggsfield.isPending ? "Probando…" : "Probar"}
                            </span>
                        </Btn>
                        {hf.configured && (
                            <Btn
                                testId="higgsfield-clear-btn"
                                variant="danger"
                                onClick={() => save.mutate({ higgsfield_key_id: "", higgsfield_key_secret: "" })}
                            >
                                Quitar
                            </Btn>
                        )}
                    </div>
                </Panel>

                {/* ---- Google / YouTube ---- */}
                <Panel
                    title="YouTube · Google Cloud OAuth"
                    testId="google-panel"
                    right={<Youtube size={15} className="text-primary" />}
                >
                    <StatusLine
                        configured={g.configured}
                        status={g.configured ? (g.connected_channels > 0 ? "ok" : null) : null}
                        detail={g.connected_channels > 0 ? `${g.connected_channels} canal(es) real(es)` : null}
                    />
                    <p className="mb-3 text-xs text-muted-foreground">
                        Crea credenciales OAuth 2.0 (app web) en Google Cloud Console con las APIs{" "}
                        <span className="text-primary">YouTube Data v3</span> y{" "}
                        <span className="text-primary">YouTube Analytics</span> habilitadas.
                    </p>
                    <div className="mb-3 border border-primary/40 p-2 text-[11px] text-muted-foreground" data-testid="google-multi-channel-note">
                        <span className="text-primary">Una sola credencial sirve para TODOS tus canales.</span>{" "}
                        La Redirect URI es fija (pertenece a tu app de Google Cloud, no al canal). Para vincular
                        varios canales pulsa &quot;Vincular canal&quot; una vez por cada cuenta/canal: cada uno guarda sus
                        propios tokens cifrados.
                    </div>
                    <div className="mb-3 border border-border p-2">
                        <div className="label mb-1">Redirect URI (regístrala en Google Cloud)</div>
                        <div className="flex items-center justify-between gap-2">
                            <span className="break-all font-mono text-[10px] text-primary">{redirectUri}</span>
                            <button
                                data-testid="copy-redirect-uri"
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(redirectUri);
                                        toast.success("Redirect URI copiada");
                                    } catch {
                                        const ta = document.createElement("textarea");
                                        ta.value = redirectUri;
                                        document.body.appendChild(ta);
                                        ta.select();
                                        const ok = document.execCommand("copy");
                                        ta.remove();
                                        if (ok) toast.success("Redirect URI copiada");
                                        else toast.error("No se pudo copiar; selecciónala manualmente");
                                    }
                                }}
                                className="shrink-0 border border-border p-1.5 text-muted-foreground hover:border-primary hover:text-primary"
                            >
                                <Copy size={12} />
                            </button>
                        </div>
                    </div>
                    <Field
                        label="Client ID"
                        value={gid}
                        onChange={setGid}
                        placeholder="xxxx.apps.googleusercontent.com"
                        testId="google-client-id-input"
                    />
                    <Field
                        label="Client Secret"
                        value={gsecret}
                        onChange={setGsecret}
                        placeholder={g.secret_mask ? `Guardado (${g.secret_mask})` : "GOCSPX-..."}
                        type="password"
                        testId="google-secret-input"
                    />
                    <div className="flex flex-wrap gap-2">
                        <Btn
                            testId="google-save-btn"
                            onClick={() => save.mutate({ google_client_id: gid, google_client_secret: gsecret })}
                            disabled={save.isPending || !gid.trim()}
                        >
                            Guardar
                        </Btn>
                        <Btn
                            testId="google-oauth-btn"
                            variant="green"
                            onClick={() => oauth.mutate()}
                            disabled={oauth.isPending || !g.configured}
                        >
                            <span className="flex items-center gap-2">
                                <ExternalLink size={13} /> Vincular canal
                            </span>
                        </Btn>
                    </div>
                </Panel>

                {/* ---- Pixelle ---- */}
                <Panel
                    title="Pixelle-Video · Motor VPS"
                    testId="pixelle-panel"
                    right={<Clapperboard size={15} className="text-primary" />}
                >
                    <StatusLine configured={px.configured} status={px.status} detail={px.detail} />
                    <p className="mb-4 text-xs text-muted-foreground">
                        Apunta a tu microservicio Pixelle-Video (AIDC-AI) del VPS. Con la URL configurada,
                        cada producción lanza <span className="text-primary">POST /api/video/generate</span> real
                        y se monitorea vía <span className="text-primary">/api/tasks/:id</span>. Sin URL, el
                        pipeline se simula.
                    </p>
                    <Field
                        label="URL base del microservicio"
                        value={pxUrl}
                        onChange={setPxUrl}
                        placeholder="http://mi-vps:8000"
                        testId="pixelle-url-input"
                    />
                    <Field
                        label="API key (opcional)"
                        value={pxKey}
                        onChange={setPxKey}
                        placeholder="Bearer token si tu servicio lo exige"
                        type="password"
                        testId="pixelle-key-input"
                    />
                    <div className="flex gap-2">
                        <Btn
                            testId="pixelle-save-btn"
                            onClick={() => save.mutate({ pixelle_base_url: pxUrl, pixelle_api_key: pxKey })}
                            disabled={save.isPending || !pxUrl.trim()}
                        >
                            Guardar
                        </Btn>
                        <Btn
                            testId="pixelle-test-btn"
                            variant="ghost"
                            onClick={() => testPx.mutate()}
                            disabled={testPx.isPending || !px.configured}
                        >
                            <span className="flex items-center gap-2">
                                <Zap size={13} /> {testPx.isPending ? "Probando…" : "Health check"}
                            </span>
                        </Btn>
                        {px.configured && (
                            <Btn
                                testId="pixelle-clear-btn"
                                variant="danger"
                                onClick={() => save.mutate({ pixelle_base_url: "", pixelle_api_key: "" })}
                            >
                                Quitar
                            </Btn>
                        )}
                    </div>
                </Panel>
            </div>
        </div>
    );
}
